import { useState, useMemo } from 'react';
import { useCareerPath } from '../hooks/useCareerPath';
import { careerService } from '../services/career.service';
import { CareerPathCard } from '../components/career/CareerPathCard';
import { CareerStepItem } from '../components/career/CareerStepItem';
import { CareerPath } from '../types/career.types';
import '../styles/career.css';

type CategoryFilter = 'all' | 'software' | 'math_science' | 'space_nature' | 'art_music_lang';

const CATEGORIES: { id: CategoryFilter; label: string; icon: string }[] = [
  { id: 'all', label: 'Tüm Kariyerler', icon: '🌟' },
  { id: 'software', label: 'Yazılım & Teknoloji', icon: '💻' },
  { id: 'math_science', label: 'Matematik & Veri', icon: '📐' },
  { id: 'space_nature', label: 'Uzay, Doğa & Robotik', icon: '🚀' },
  { id: 'art_music_lang', label: 'Sanat, Dil & Müzik', icon: '🎨' },
];

function getCategoryForCareer(career: CareerPath): CategoryFilter {
  const title = career.title.toLowerCase();
  const skills = (career.required_skills || []).map((s) => s.toLowerCase());

  if (
    title.includes('oyun') ||
    title.includes('web') ||
    title.includes('mobil') ||
    title.includes('siber') ||
    skills.includes('html') ||
    skills.includes('javascript') ||
    skills.includes('css')
  ) {
    return 'software';
  }
  if (
    title.includes('matematik') ||
    title.includes('yapay zeka') ||
    skills.includes('matematik') ||
    skills.includes('veri-analizi') ||
    skills.includes('ai-data')
  ) {
    return 'math_science';
  }
  if (
    title.includes('uzay') ||
    title.includes('çevre') ||
    title.includes('robotik') ||
    skills.includes('astronomi') ||
    skills.includes('biyoloji') ||
    skills.includes('robotik') ||
    skills.includes('fizik')
  ) {
    return 'space_nature';
  }
  if (
    title.includes('sanat') ||
    title.includes('dil') ||
    title.includes('müzik') ||
    skills.includes('resim') ||
    skills.includes('ingilizce') ||
    skills.includes('muzik')
  ) {
    return 'art_music_lang';
  }
  return 'software';
}

export function CareerPage() {
  const {
    careerPaths,
    isLoadingPaths,
    selectedCareer,
    selectCareer,
    activePath,
    isLoadingActivePath,
    isGenerating,
    generatePath,
    error,
  } = useCareerPath();

  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all');
  const [motivation, setMotivation] = useState<string | null>(null);
  const [motivationLoading, setMotivationLoading] = useState(false);

  // motivation artık otomatik yüklenmiyor — kullanıcı butona tıklayana kadar boşa istek atılmaz
  function handleGetMotivation() {
    if (!selectedCareer || motivationLoading) return;
    setMotivationLoading(true);
    careerService
      .getMotivation(selectedCareer.id)
      .then(setMotivation)
      .catch(() => setMotivation(null))
      .finally(() => setMotivationLoading(false));
  }

  const filteredCareerPaths = useMemo(() => {
    if (activeCategory === 'all') return careerPaths;
    return careerPaths.filter((career) => getCategoryForCareer(career) === activeCategory);
  }, [careerPaths, activeCategory]);

  const completedSteps = activePath?.steps.filter((s) => s.status === 'completed').length ?? 0;
  const totalSteps = activePath?.steps.length ?? 0;
  const totalMonths = activePath?.steps.reduce((sum, s) => sum + s.durationMonths, 0) ?? 0;
  const progressPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  return (
    <div className="career-page">
      <header className="career-header">
        <h1>Kariyer Haritası 🚀</h1>
        <p>Geleceğin mesleklerini keşfet! İlgi duyduğun kariyer alanına tıkla, AI yol haritan anında oluşsun.</p>
      </header>

      {/* Kategori Filtre Butonları */}
      <div className="career-category-tabs">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={`career-category-tab ${activeCategory === cat.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            <span className="career-category-icon">{cat.icon}</span>
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {error && <div className="quiz-error-banner" style={{ marginBottom: 20 }}>{error}</div>}

      {isLoadingPaths ? (
        <div className="courses-loading">Kariyer fırsatları yükleniyor...</div>
      ) : (
        <div className="career-select-grid">
          {filteredCareerPaths.map((career) => (
            <CareerPathCard
              key={career.id}
              career={career}
              isSelected={selectedCareer?.id === career.id}
              onSelect={() => selectCareer(career)}
            />
          ))}
        </div>
      )}

      {selectedCareer && (
        <div style={{ marginTop: 32 }} id="career-roadmap-section">
          <div style={{ marginBottom: 16 }}>
            {motivation ? (
              <div className="card" style={{ borderLeft: '4px solid var(--accent)', marginBottom: 12 }}>
                <p style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>{motivation}</p>
              </div>
            ) : (
              <button
                type="button"
                className="lesson-btn secondary sm"
                onClick={handleGetMotivation}
                disabled={motivationLoading}
                style={{ marginBottom: 12 }}
              >
                {motivationLoading ? '⏳ Yükleniyor...' : '✨ AI Motivasyon Mesajı Al'}
              </button>
            )}
          </div>

          {(isLoadingActivePath || isGenerating) && (
            <div className="card career-empty-state career-loading-card">
              <div className="career-loading-spinner">{selectedCareer.icon ?? '✨'}</div>
              <h3>"{selectedCareer.title}" Yol Haritası Hazırlanıyor...</h3>
              <p>AI Mentor, seviyene ve hedeflerine göre sana özel öğrenme adımlarını çıkarıyor.</p>
              <div className="career-progress-loader">
                <div className="career-progress-loader-fill" />
              </div>
            </div>
          )}

          {!isLoadingActivePath && !isGenerating && !activePath && (
            <div className="card career-empty-state">
              <div className="career-empty-icon">{selectedCareer.icon ?? '🚀'}</div>
              <h3>"{selectedCareer.title}" için yol haritası oluşturulamadı</h3>
              <p>AI Mentor şu an yol haritanı hazırlayamadı. Lütfen tekrar deneyin.</p>
              <button type="button" className="lesson-btn primary" onClick={generatePath}>
                ✨ AI ile Yol Haritasını Tekrar Dene
              </button>
            </div>
          )}

          {!isLoadingActivePath && !isGenerating && activePath && (
            <>
              <div className="card" style={{ marginBottom: 24 }}>
                <div className="career-goal-row">
                  <div className="career-goal-icon">{selectedCareer.icon ?? '🚀'}</div>
                  <div>
                    <h3>Hedef: {activePath.careerTitle}</h3>
                    <p>İlgi alanlarına göre AI tarafından kişiselleştirilmiş öğrenme adımları</p>
                  </div>
                </div>
                <div className="career-goal-stats">
                  <div className="career-goal-stat-row">
                    <span>Tahmini Süre</span>
                    <span className="career-goal-stat-value">{totalMonths} Ay</span>
                  </div>
                  <div className="career-goal-stat-row">
                    <span>Tamamlanan</span>
                    <span className="career-goal-stat-value success">
                      {completedSteps}/{totalSteps} Adım
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }} />
                  </div>
                </div>
              </div>

              <div className="career-path">
                {activePath.steps.map((step, i) => (
                  <CareerStepItem key={`${step.title}-${i}`} step={step} index={i} />
                ))}
              </div>

              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <button
                  type="button"
                  className="lesson-btn secondary sm"
                  onClick={generatePath}
                  disabled={isGenerating}
                >
                  {isGenerating ? '⏳ Yeniden oluşturuluyor...' : '🔄 Yol Haritasını Yeniden Oluştur'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
